import React, { useCallback, useEffect, useMemo, useState } from "react";

import { isUndefined } from "lodash";
import { useMatch, useNavigate } from "react-router-dom";
import { useTypedParams } from "react-router-typesafe-routes/dom";
import { ROUTES } from "src/routes";

import { Participant, useSearchParticipantsQuery } from "@api/graphql";
import AccountDetails from "@components/account_profile/AccountDetails";
import AccountRelationships from "@components/account_profile/AccountRelationships";
import CurrentRoyalties from "@components/account_profile/CurrentRoyalties";
import ParticipantInfo from "@components/account_profile/ParticipantInfo";
import PastResaleReportsComponent from "@components/account_profile/PastResaleReports";
import PastRoyalties from "@components/account_profile/PastRoyalties";
import PendingPaymentsComponent from "@components/account_profile/PendingPayments";
import { TabNavigation, TabProps } from "@components/account_profile/TabNavigation";
import UnderReviewResalesComponent from "@components/account_profile/UnderReviewResales";
import { useIsAuthUserAllowed } from "@components/permission_control/ProtectedComponent";
import { useAuthenticationContext } from "@context/authentication";
import styles from "@pages/dashboard/AccountProfile.module.scss";
import { DashboardHeader } from "@pages/dashboard/DashboardHeader";
import { Role } from "@roles";
import { isAMP, isAgent, isArtCentre, isArtistOrBeneficiary, isCMO } from "@util/index";

export const AccountProfile = () => {
  const { auth } = useAuthenticationContext();
  const navigate = useNavigate();
  const { participantId } = useTypedParams(ROUTES.Profile);
  const isAdminUser = useIsAuthUserAllowed([Role.VisualArtistAdmin, Role.MemberServices, Role.Data]);
  const isRoyaltyRecipient = useIsAuthUserAllowed([Role.RoyaltyRecipient]);
  const isDataUser = useIsAuthUserAllowed([Role.Data]);
  const canViewAccountRelationships = isAdminUser || isRoyaltyRecipient || isDataUser;
  const isDashboardPath = useMatch(`${ROUTES.Dashboard.path}/*`);
  const [participantData, setParticipantData] = useState<Participant | undefined>(undefined);

  const { data, loading } = useSearchParticipantsQuery({
    variables: {
      filter_by: [{ field: "participant_id", eq: participantId?.toString() || "" }],
    },
    skip: !participantId,
  });

  useEffect(() => {
    setParticipantData(data?.searchParticipants?.[0]);
  }, [data]);

  useEffect(() => {
    const currentParticipantId = auth?.currentUserData?.participant?.participant_id;

    if (!currentParticipantId) {
      console.warn("No participant ID found in current user data.");
      return;
    }
    const isMissingParticipantId = isUndefined(participantId);
    const isUnauthorizedUser = !isAdminUser && participantId !== currentParticipantId;

    if (isDashboardPath && isMissingParticipantId) {
      console.info("Redirecting to dashboard profile with current participant ID");
      navigate(ROUTES.Dashboard.Profile.buildPath({ participantId: currentParticipantId }), {
        replace: true,
      });
    } else if (!isDashboardPath && (isMissingParticipantId || isUnauthorizedUser)) {
      console.info("Redirecting to current user's profile");
      navigate(ROUTES.Profile.buildPath({ participantId: currentParticipantId }), { replace: true });
    }
  }, [participantId, auth, navigate, isDashboardPath, isAdminUser]);

  const getTabRoute = useCallback(
    (profileRoute: string, dashboardRoute: string) => {
      return isDashboardPath ? dashboardRoute : profileRoute;
    },
    [isDashboardPath],
  );

  const tabs = useMemo(() => {
    if (!participantData?.participant_types) {
      return [];
    }

    const participantTypes = participantData.participant_types;
    const isArtistBeneficiary = isArtistOrBeneficiary(participantTypes);
    const isArtCentreCMO = isArtCentre(participantTypes) || isCMO(participantTypes) || isAgent(participantTypes);
    const isAMPType = isAMP(participantTypes);

    const baseTabs: TabProps[] = [
      {
        route: getTabRoute(
          ROUTES.Profile.buildPath({ participantId }),
          ROUTES.Dashboard.Profile.buildPath({ participantId }),
        ),
        displayName: "Account details",
        component: AccountDetails,
      },
    ];

    if (canViewAccountRelationships) {
      baseTabs.push({
        route: getTabRoute(
          ROUTES.Profile.AccountRelationships.buildPath({ participantId }),
          ROUTES.Dashboard.Profile.AccountRelationships.buildPath({ participantId }),
        ),
        displayName: "Account relationships",
        component: AccountRelationships,
      });
    }

    if (isArtistBeneficiary || isArtCentreCMO) {
      baseTabs.push(
        {
          route: getTabRoute(
            ROUTES.Profile.CurrentRoyalties.buildPath({ participantId }),
            ROUTES.Dashboard.Profile.CurrentRoyalties.buildPath({ participantId }),
          ),
          displayName: "Future royalties",
          component: CurrentRoyalties,
        },
        {
          route: getTabRoute(
            ROUTES.Profile.PastRoyalties.buildPath({ participantId }),
            ROUTES.Dashboard.Profile.PastRoyalties.buildPath({ participantId }),
          ),
          displayName: "Past royalties",
          component: PastRoyalties,
        },
      );
    }

    if (isArtCentreCMO || isAMPType) {
      baseTabs.push(
        {
          route: getTabRoute(
            ROUTES.Profile.UnderReview.buildPath({ participantId }),
            ROUTES.Dashboard.Profile.UnderReview.buildPath({ participantId }),
          ),
          displayName: "Under review",
          component: UnderReviewResalesComponent,
        },
        {
          route: getTabRoute(
            ROUTES.Profile.PendingPayments.buildPath({ participantId }),
            ROUTES.Dashboard.Profile.PendingPayments.buildPath({ participantId }),
          ),
          displayName: "Pending payments",
          component: PendingPaymentsComponent,
        },
        {
          route: getTabRoute(
            ROUTES.Profile.PastResaleReports.buildPath({ participantId }),
            ROUTES.Dashboard.Profile.PastResaleReports.buildPath({ participantId }),
          ),
          displayName: "Past resale reports",
          component: PastResaleReportsComponent,
        },
      );
    }

    return baseTabs;
  }, [participantData, getTabRoute, participantId, canViewAccountRelationships]);

  return (
    <>
      {!isDashboardPath && <DashboardHeader />}
      {participantId && (
        <div className={styles.wrapper}>
          <ParticipantInfo participantId={participantId} isAdminUser={isAdminUser} />
          {loading ? <div>Loading...</div> : <TabNavigation tabs={tabs} />}
        </div>
      )}
    </>
  );
};
